'use client';

import { useEffect, useRef, useState } from 'react';
import { SKILL_CATEGORIES, TECH_BADGES } from '@/data/portfolioData';
import SectionHeader from './SectionHeader';

const PROFICIENCY_COLORS: Record<string, string> = {
  Proficient: '#a0785a',
  Advanced: '#c9a87c',
  Intermediate: '#7d9b8a',
  Familiar: '#a39e96',
};

function SkillItem({
  name,
  level,
}: {
  name: string;
  level: string;
}) {
  const levelColor = PROFICIENCY_COLORS[level] || '#c9a87c';

  return (
    <div className="group py-4 border-b border-parchment/6 last:border-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <span className="text-parchment text-base group-hover:text-sand transition-colors duration-300">
          {name}
        </span>
        <span
          className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full"
          style={{
            color: levelColor,
            backgroundColor: `${levelColor}15`,
            border: `1px solid ${levelColor}30`,
          }}
        >
          {level}
        </span>
      </div>
      <div className="h-px bg-parchment/8 group-hover:bg-sand/20 transition-colors duration-500" />
    </div>
  );
}

export default function SkillsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeCategory, setActiveCategory] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.08 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const activeCat = SKILL_CATEGORIES[activeCategory];

  return (
    <section id="skills" ref={sectionRef} className="py-28 xl:py-40 relative">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
        <div
          className={`transition-all duration-800 ease-organic ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <SectionHeader index="02 — Skills" title="My" highlight="toolkit" />
        </div>

        {/* Asymmetric layout: badges left, skills right with offset */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 xl:gap-16 items-start">
          <div
            className={`lg:col-span-4 xl:col-span-5 transition-all duration-800 delay-100 ease-organic ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
            }`}
          >
            <div className="warm-card rounded-2xl p-6 xl:p-8 mb-6">
              <h3 className="font-display text-parchment text-sm font-600 mb-6">
                Domain overview
              </h3>
              <div className="space-y-4">
                {SKILL_CATEGORIES.map((cat, i) => (
                  <button
                    key={cat.category}
                    type="button"
                    onClick={() => setActiveCategory(i)}
                    className={`w-full flex items-center justify-between py-3 px-4 rounded-xl text-left transition-all duration-300 ${
                      activeCategory === i
                        ? 'bg-sand/10 border border-sand/20'
                        : 'hover:bg-parchment/5 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-2 h-2 rounded-full transition-transform duration-300"
                        style={{
                          backgroundColor: cat.color,
                          transform: activeCategory === i ? 'scale(1.25)' : 'scale(1)',
                        }}
                      />
                      <span
                        className={`text-sm transition-colors duration-300 ${
                          activeCategory === i ? 'text-sand' : 'text-parchment-muted'
                        }`}
                      >
                        {cat.category}
                      </span>
                    </div>
                    <span className="font-mono text-xs text-parchment-muted">
                      {cat.skills.length}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="warm-card rounded-2xl p-6">
              <p className="font-mono text-[10px] text-parchment-muted uppercase tracking-widest mb-4">
                Technologies
              </p>
              <div className="flex flex-wrap gap-2">
                {TECH_BADGES.map((tech) => (
                  <span
                    key={tech}
                    className="px-3 py-1.5 rounded-lg text-xs font-mono text-parchment-muted border border-parchment/8 bg-parchment/[0.02] hover:border-sand/25 hover:text-sand transition-all duration-300 cursor-default"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div
            className={`lg:col-span-8 xl:col-span-7 lg:mt-12 transition-all duration-800 delay-200 ease-organic ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'
            }`}
          >
            <div className="warm-card rounded-2xl p-8 xl:p-10">
              <div className="flex items-end justify-between gap-4 mb-8 pb-6 border-b border-parchment/8">
                <div>
                  <p className="font-mono text-[10px] text-sand uppercase tracking-widest mb-2">
                    {activeCat.category}
                  </p>
                  <h3 className="font-display text-2xl text-parchment font-700">
                    {activeCat.skills.length} skills
                  </h3>
                </div>
                <div
                  className="w-12 h-12 rounded-full border-2 flex items-center justify-center font-mono text-sm"
                  style={{ borderColor: `${activeCat.color}40`, color: activeCat.color }}
                >
                  {activeCat.skills.length}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10">
                {activeCat.skills.map((skill) => (
                  <SkillItem key={skill.name} name={skill.name} level={skill.level} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
